/**
 * Canonical itinerary expansion + day-grouping engine.
 *
 * This is the SINGLE source of truth for turning itinerary items into the
 * display events and day structure shown to the traveler. It is shared by:
 *
 *  - the web client (reactive render in `ItineraryState` / `ItineraryView`),
 *  - the server `ListItineraryEvents` flow (pre-expanded events over the wire
 *    for native clients), and
 *  - the server day-summaries prompt builder, so the AI "Day N" titles line up
 *    with the rendered "Day N".
 *
 * Previously the client expanded items locally (local date keys) while the
 * server fanned items out independently (UTC keys), so AI day titles could be
 * denormalized against a different day than the one actually rendered. This
 * module collapses that divergence: every consumer feeds the same normalized
 * items in and gets the same events, day keys, day numbers and travel-day flags
 * out.
 *
 * Inputs are pre-normalized (`ExpandableItem`): callers resolve the heavy,
 * environment-specific bits (the flight option, the hotel id/name, the
 * denormalized place `eventType`) before calling in. Date/time *formatting*
 * stays with the caller — this module only deals in raw ISO strings and
 * calendar-date keys.
 */

import type { FlightOption, FlightSegment } from './schemas/flight-schemas.js';
import type {
  ItineraryItemEvent,
  ItineraryItemEventType,
} from './schemas/itinerary-item-event-schemas.js';
import type { BookingStatus, ItineraryItemType } from './schemas/itinerary-item-schemas.js';
import type { Cost } from './schemas/shared-schemas.js';

import { addDays, differenceInDays, differenceInMinutes, format, isValid, parseISO } from 'date-fns';

import { aggregateBookingStatus } from '../booking-status.js';

const MIN_LAYOVER_DURATION_MINUTES = 30;

/**
 * Normalized itinerary item consumed by the expansion engine. Callers resolve
 * the heavy referenced data (flight option, hotel identity, place event type)
 * into these plain fields before expanding.
 */
export interface ExpandableItem {
  id: string;
  type: ItineraryItemType;
  title: string;
  startDateTime: string;
  endDateTime?: string;
  /** Resolved flight option (segments / outbound / return) for `type: 'flight'`. */
  flightOption?: FlightOption;
  /** Hotel grouping identity for `type: 'room'`. Falls back to the item id. */
  hotelId?: string;
  /** Hotel display name for `type: 'room'`. Falls back to "Hotel". */
  hotelName?: string;
  /**
   * Denormalized event type for `type: 'place'` (e.g. `place_dining`), resolved
   * upstream from the place document (ELARA-680). When absent the engine falls
   * back to the raw item type.
   */
  placeEventType?: ItineraryItemEventType;
  /**
   * The place's own Google primary type for `type: 'place'` (e.g. `irish_pub`), resolved upstream from
   * the place document. Denormalized onto the expanded event's metadata so clients can name it by what
   * it is ("Irish pub") instead of the broad category it falls into ("Nightlife"). Absent for
   * activity/custom items and places with no type.
   */
  placePrimaryType?: string;
  /** Destination id of this room's hotel, resolved upstream from `hotel.destination` (`type: 'room'`). */
  hotelDestinationId?: string;
  /** Destination name of this room's hotel (`type: 'room'`). */
  hotelDestinationName?: string;
  /** Country code of this room's hotel destination (`type: 'room'`). */
  hotelCountryCode?: string;
  /** City of this room's hotel, from `hotel.address.city` (`type: 'room'`). */
  hotelCity?: string;
  /**
   * Resolved per-item cost for destination totals, resolved upstream (flight price,
   * room rate total, or a manual cost). Absent for items with no resolvable price.
   */
  cost?: Cost;
  /** This item's booking status, resolved upstream from `booking.status`. Drives the event's status badge. */
  bookingStatus?: BookingStatus;
}

/**
 * A destination a stretch of itinerary days belongs to. Sourced from a hotel's
 * pre-resolved `destination` reference, a flight's arrival airport, or the trip's
 * working-memory-derived plan. Fields are best-effort — callers label from
 * `name`/`cityName`/`countryName` and may hydrate a full `Destination` via the id.
 */
export interface DestinationDescriptor {
  /** Resolved destination doc id (from a hotel's `destination` reference or the trip plan). Best join key. */
  destinationId?: string;
  /** Display name (hotel destination name, flight country/city name, or plan name). */
  name?: string;
  cityName?: string;
  countryCode?: string;
  countryName?: string;
}

/** A planned destination with its date window, resolved from `Trip.destinationPlan`. */
export interface PlannedDestination {
  descriptor: DestinationDescriptor;
  /** Inclusive YYYY-MM-DD date keys bounding the planned stay. */
  startDateKey: string;
  endDateKey: string;
}

/**
 * A contiguous run of calendar days the traveler spends at one destination,
 * with its date window and per-currency cost subtotals.
 */
export interface DestinationGroup {
  /** Stable key used to coalesce consecutive days: destinationId ?? `${countryCode}:${cityName}` ?? 'origin'. */
  key: string;
  /** Resolved destination for this run; undefined only for a no-signal trip (no hotels, flights, or plan). */
  descriptor?: DestinationDescriptor;
  /** Inclusive ordered date keys (YYYY-MM-DD) for this group; a contiguous slice of the trip's days. */
  dateKeys: string[];
  startDateKey: string;
  endDateKey: string;
  /** What anchored this group's destination; undefined for a no-signal "origin" group. */
  source?: 'hotel' | 'flight' | 'plan';
  /** Per-currency cost subtotals for items in this group ([] when none carry a cost). One Cost per currency — NO conversion. */
  costs: Cost[];
}

/** Canonical calendar day in a trip's itinerary. */
export interface ItineraryDay {
  /** Local calendar-date key (YYYY-MM-DD), derived from the event date strings. */
  dateKey: string;
  /** 1-based sequential day number used for "Day N" labels. */
  dayNumber: number;
  /**
   * True when this day has no events of its own and the previous day ends on a
   * flight departure (the traveler is in the air / crossed a date line).
   */
  isTravelDay: boolean;
}

/**
 * Extracts the local calendar-date key (YYYY-MM-DD) from an ISO/local datetime
 * string by reading the date part directly (no timezone math), so the rendered
 * day matches the time the traveler sees.
 */
export function extractEventDateKey(dateTimeString: string): string | undefined {
  if (!dateTimeString) return undefined;
  let datePart: string;
  if (dateTimeString.includes('T')) {
    datePart = dateTimeString.split('T')[0]!;
  } else if (dateTimeString.includes(' ')) {
    datePart = dateTimeString.split(' ')[0]!;
  } else {
    datePart = dateTimeString;
  }
  return /^\d{4}-\d{2}-\d{2}$/.test(datePart) ? datePart : undefined;
}

/**
 * Strips a trailing timezone designator (`Z` or `±HH:mm` / `±HHmm`) from an
 * ISO-ish datetime string and normalizes any space date/time separator to `T`,
 * yielding a bare wall-clock string (e.g. `2026-07-21T09:15:00`). Values without
 * a recognizable offset are returned with only the separator normalized.
 *
 * A bare `YYYY-MM-DD` (no time part — e.g. a room whose hotel has no recorded
 * check-in/check-out time, see `normalizeItineraryItemDates`) is given an explicit
 * `T00:00:00` so it compares as *equal to*, not lexicographically less than, another
 * event pinned to midnight on the same day (e.g. `hotel_checkout`'s forced
 * `${date}T00:00:00`). Without this, `"2025-03-06"` — being a string-prefix of
 * `"2025-03-06T00:00:00"` — sorts before it, and the `sortPriority` tiebreaker
 * (meant to guarantee checkout-before-checkin on a same-day hotel changeover) never
 * gets a chance to run because the two strings are never exactly equal.
 *
 * Expects well-formed input as produced by `combineDateAndTime`, AI tools, and
 * demo data (`YYYY-MM-DD[T| ]HH:mm:ss[Z|±HH:mm]`); it normalizes rather than
 * validates, so the lexicographic compare stays deterministic on odd input.
 */
function toWallClock(dateTimeString: string): string {
  const normalized = dateTimeString.replaceAll(' ', 'T').replace(/(?:Z|[+-]\d{2}:?\d{2})$/, '');
  return normalized.includes('T') ? normalized : `${normalized}T00:00:00`;
}

/**
 * Compare two itinerary datetime strings chronologically on **wall-clock** time,
 * ignoring any timezone offset.
 *
 * Itinerary times are inherently local to the destination ("9 AM in Kyoto"), and
 * both the display (`formatTimeInOriginalTimezone`) and the day grouping
 * (`extractEventDateKey`) already read these strings as wall-clock. The sort must
 * use the same basis — otherwise an offset-less edited time (`…T09:15:00`, as
 * produced by `combineDateAndTime`) is compared as a different absolute instant
 * than its offset-bearing siblings (`…T11:00:00+09:00`) and the row lands out of
 * chronological order, with the landing slot varying by the viewer's timezone
 * (ELARA-802).
 *
 * Canonical ISO wall-clock strings (`YYYY-MM-DDTHH:mm:ss`) sort lexicographically
 * in chronological order, so this comparison is both correct and independent of
 * the viewer's timezone. Flight events that carry genuine cross-timezone offsets
 * are grouped onto their own local date by `extractEventDateKey`, so within-day
 * ordering stays wall-clock too.
 */
export function compareDateTimes(a: string, b: string): number {
  const wallClockA = toWallClock(a);
  const wallClockB = toWallClock(b);
  if (wallClockA < wallClockB) return -1;
  if (wallClockA > wallClockB) return 1;
  return 0;
}

/**
 * Compare two itinerary events by dateTime, then by sortPriority as tiebreaker.
 * Lower sortPriority values sort first. Default priority is 0.
 */
export function compareEvents(
  a: { dateTime: string; sortPriority?: number },
  b: { dateTime: string; sortPriority?: number },
): number {
  const timeComparison = compareDateTimes(a.dateTime, b.dateTime);
  if (timeComparison !== 0) return timeComparison;
  return (a.sortPriority ?? 0) - (b.sortPriority ?? 0);
}

/** Checks if an event spans multiple calendar days. */
export function isMultiDay(startDateTime: string, endDateTime?: string): boolean {
  if (!endDateTime) return false;
  const start = parseISO(startDateTime);
  const end = parseISO(endDateTime);
  if (!isValid(start) || !isValid(end)) return false;
  return format(start, 'yyyy-MM-dd') !== format(end, 'yyyy-MM-dd');
}

/** Generates a deterministic event id from its source item. */
export function generateEventId(itemId: string, eventType: string, index?: number): string {
  return index === undefined ? `${itemId}__${eventType}` : `${itemId}__${eventType}_${index}`;
}

/** Calculates layover duration in minutes between two flight segments. */
export function calculateLayoverDuration(arrivalDateTime: string, departureDateTime: string): number {
  const arrival = parseISO(arrivalDateTime);
  const departure = parseISO(departureDateTime);
  if (!isValid(arrival) || !isValid(departure)) return 0;
  return differenceInMinutes(departure, arrival);
}

/** Formats a duration in minutes to a human-readable string (e.g. "2h 15m"). */
function formatDurationMinutes(minutes: number): string {
  if (!minutes || minutes < 0) return '';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  return `${mins}m`;
}

/**
 * @param isPrimaryGroup - Whether this call's first segment is the item's overall primary "Book"
 * leg. True for the outbound segments (or the legacy flat `segments` array when that's what the
 * option uses), always false for `returnSegments` — a round trip is one booking, so only the
 * outbound leg's card should ever offer the "Book" action, never the return leg's.
 */
function eventsForFlightSegments(segments: FlightSegment[], itemId: string, isPrimaryGroup: boolean): ItineraryItemEvent[] {
  const events: ItineraryItemEvent[] = [];
  if (segments.length === 0) return events;

  for (const [i, segment] of segments.entries()) {
    // Skip segment if required fields are missing
    if (!segment.origin || !segment.destination || !segment.departure || !segment.arrival) {
      continue;
    }

    // Create departure event; create arrival event only when the flight is overnight (different calendar day)
    const isOvernight = segment.overnight;

    const segmentEvents: ItineraryItemEvent[] = [
      {
        id: generateEventId(itemId, `${segment.origin.iata_code}_${segment.destination.iata_code}_departure`, i),
        title: `${segment.airline.name} flight to ${segment.destination.city_name ?? segment.destination.airport_name} (${segment.flightNumber})`,
        dateTime: segment.departure,
        type: 'flight_departure',
        itemId,
        metadata: { segment, isFirstSegment: isPrimaryGroup && i === 0 },
      },
      ...(isOvernight
        ? [
            {
              id: generateEventId(itemId, `${segment.origin.iata_code}_${segment.destination.iata_code}_arrival`, i),
              title: segment.destination.city_name ? `Arrive in ${segment.destination.city_name}` : `Arrive at ${segment.destination.airport_name} (${segment.destination.iata_code || ''})`,
              dateTime: segment.arrival,
              type: 'flight_arrival' as const,
              itemId,
              metadata: { segment },
            },
          ]
        : []),
    ];

    events.push(...segmentEvents);

    // Create layover event if this is not the last segment
    if (i < segments.length - 1) {
      const nextSegment = segments[i + 1];
      if (nextSegment?.departure && segment.arrival && segment.destination) {
        const layoverDuration = calculateLayoverDuration(segment.arrival, nextSegment.departure);

        // Only create layover event if there's a meaningful duration (more than 30 minutes)
        if (layoverDuration > MIN_LAYOVER_DURATION_MINUTES) {
          events.push({
            id: generateEventId(
              itemId,
              `${nextSegment.origin.iata_code}_${nextSegment.destination.iata_code}_layover`,
              i,
            ),
            title: `${formatDurationMinutes(layoverDuration)} layover at ${segment.destination.airport_name} (${segment.destination.iata_code || ''})`,
            dateTime: segment.arrival, // Use arrival time as the layover start
            type: 'layover',
            itemId,
            // For a layover, "segment" should represent the next flight leg after the layover.
            metadata: { duration: layoverDuration, segment: nextSegment },
          });
        }
      }
    }
  }

  return events;
}

/**
 * Expands a single normalized itinerary item into its display events.
 *
 * Rooms expand to nothing here — hotel check-in/out events are produced as a
 * group by {@link createGroupedHotelEvents} so a multi-room stay collapses to
 * one check-in and one check-out.
 */
export function expandItineraryItem(item: ExpandableItem): ItineraryItemEvent[] {
  const events: ItineraryItemEvent[] = [];
  if (!item.id) throw new Error('Item ID is required');

  switch (item.type) {
    case 'flight': {
      const flightOption = item.flightOption;
      if (!flightOption) break;

      // The outbound leg (legacy flat `segments`, or `outboundSegments` for the current shape) is
      // the item's primary "Book" leg; `returnSegments` never is — a round trip books as one item.
      const hasLegacySegments = (flightOption.segments?.length ?? 0) > 0;
      events.push(
        ...eventsForFlightSegments(flightOption.segments ?? [], item.id, hasLegacySegments),
        ...eventsForFlightSegments(flightOption.outboundSegments ?? [], item.id, !hasLegacySegments),
        ...eventsForFlightSegments(flightOption.returnSegments ?? [], item.id, false),
      );

      // Create a dedicated destination_arrival event when the traveler
      // arrives in a different country than they departed from.
      const outboundSegments =
        flightOption.outboundSegments && flightOption.outboundSegments.length > 0
          ? flightOption.outboundSegments
          : flightOption.segments ?? [];

      if (outboundSegments.length > 0) {
        const first = outboundSegments[0]!;
        const last = outboundSegments.at(-1)!;
        const originCountry = first.origin?.country_code;
        const destinationCountry = last.destination?.country_code;

        if (originCountry && destinationCountry && originCountry !== destinationCountry) {
          events.push({
            id: generateEventId(item.id, 'destination_arrival'),
            title: `Arrive in ${last.destination.country_name ?? last.destination.city_name ?? destinationCountry}`,
            dateTime: last.arrival,
            type: 'destination_arrival',
            itemId: item.id,
            metadata: { segment: last },
          });
        }
      }
      break;
    }

    case 'room': {
      // Room items are expanded via createGroupedHotelEvents (grouped by hotel)
      break;
    }

    default: {
      // Prefer the denormalized place event type (e.g. place_dining); fall back
      // to the raw item type for activity/custom items and un-denormalized places.
      const eventType = (item.placeEventType ?? item.type) as ItineraryItemEventType;
      // Carried so the event can be named by what the place is, not just its category (see
      // `ExpandableItem.placePrimaryType`).
      const placeMetadata = item.placePrimaryType ? { placePrimaryType: item.placePrimaryType } : {};

      if (isMultiDay(item.startDateTime, item.endDateTime)) {
        // Multi-day event: create one event for each day
        const startDate = parseISO(item.startDateTime);
        const endDate = item.endDateTime ? parseISO(item.endDateTime) : null;

        if (isValid(startDate) && endDate && isValid(endDate)) {
          // All-day items store a bare `YYYY-MM-DD` (no `T`) — `formattedTime` reads that shape to
          // show "All day" rather than a clock time, so a missing time part must stay absent on every
          // expanded day, not default to a synthetic midnight (which would render as "12:00 AM").
          const timePart = item.startDateTime.split('T')[1];
          let currentDate = startDate;
          let dayIndex = 0;

          while (currentDate <= endDate) {
            const dateKey = format(currentDate, 'yyyy-MM-dd');
            const eventDateTime = timePart ? `${dateKey}T${timePart}` : dateKey;
            events.push({
              id: generateEventId(item.id, eventType, dayIndex),
              title: item.title,
              dateTime: eventDateTime,
              type: eventType,
              itemId: item.id,
              metadata: placeMetadata,
            } as ItineraryItemEvent);

            currentDate = addDays(currentDate, 1);
            dayIndex++;
          }
        }
      } else if (item.startDateTime) {
        events.push({
          id: generateEventId(item.id, eventType),
          title: item.title,
          dateTime: item.startDateTime,
          type: eventType,
          itemId: item.id,
          metadata: placeMetadata,
        } as ItineraryItemEvent);
      }
      break;
    }
  }

  const sorted = events.toSorted((a, b) => compareDateTimes(a.dateTime, b.dateTime));

  // Cost is a single whole-item figure — attach it to just the earliest event so a multi-segment
  // flight (or multi-day item) shows its price once, not on every leg/day.
  if (sorted.length > 0 && item.cost) {
    sorted[0] = { ...sorted[0]!, cost: item.cost };
  }

  // Booking status describes the WHOLE item, so every expanded event gets it — a multi-segment
  // flight renders each leg as its own row/card on both web and mobile (`isFirstSegment` on the
  // `flight_departure` metadata is what gates the primary "Book" action to just the first leg).
  if (item.bookingStatus) {
    return sorted.map((event) => ({ ...event, bookingStatus: item.bookingStatus }));
  }

  return sorted;
}

/**
 * Information about a hotel stay for a specific day, used by the itinerary
 * builder banner.
 */
export interface HotelStayInfo {
  hotelId: string;
  hotelName: string;
  /** The primary room item ID (for accessing hotel/roomOption getters) */
  itemId: string;
  /** All room item IDs for this hotel */
  itemIds: string[];
  /** Whether this is the check-in day */
  isCheckinDay: boolean;
  /** Whether this is the check-out day (last day, user is not staying this night) */
  isCheckoutDay: boolean;
  /** Destination this hotel sits in, from the hotel's pre-resolved `destination` reference. */
  destination?: DestinationDescriptor;
}

function hotelIdFor(item: ExpandableItem): string {
  return item.hotelId || item.id;
}

function hotelNameFor(item: ExpandableItem): string {
  return item.hotelName || 'Hotel';
}

/**
 * Builds a destination descriptor for a room item from its resolved hotel fields,
 * or undefined when no destination/location signal is present.
 */
function hotelDestinationFor(item: ExpandableItem): DestinationDescriptor | undefined {
  const descriptor: DestinationDescriptor = {
    ...(item.hotelDestinationId ? { destinationId: item.hotelDestinationId } : {}),
    ...(item.hotelDestinationName ? { name: item.hotelDestinationName } : {}),
    ...(item.hotelCity ? { cityName: item.hotelCity } : {}),
    ...(item.hotelCountryCode ? { countryCode: item.hotelCountryCode } : {}),
  };
  return Object.keys(descriptor).length > 0 ? descriptor : undefined;
}

/**
 * Whether an item's booking was cancelled outright. Terminal `cancelled` only: a `cancellation_pending`
 * booking may still be live at the property, so it keeps its place until the cancel is confirmed (same
 * rule as `findStayForDay` in `client-core/lib/trip-support`).
 */
export function isCancelledBooking(item: Pick<ExpandableItem, 'bookingStatus'>): boolean {
  return item.bookingStatus === 'cancelled';
}

/**
 * Groups room items by hotel id. A cancelled room is left out (ELARA-972): the stay is no longer
 * planned, so it has no check-in or check-out, names no hotel in the day headers, leaves the "find
 * places to stay" prompt for its nights, and sets no destination. Every hotel event and day-by-day stay
 * is built from these groups, so this is the one place that rule lives. The Bookings tab reads the items
 * themselves, not these events, so the cancelled booking is still listed there.
 */
function groupRoomItemsByHotel(roomItems: ExpandableItem[]): Map<string, ExpandableItem[]> {
  const byHotel = new Map<string, ExpandableItem[]>();
  for (const item of roomItems) {
    if (!item.id || item.type !== 'room' || isCancelledBooking(item)) continue;
    const hotelId = hotelIdFor(item);
    const group = byHotel.get(hotelId) ?? [];
    group.push(item);
    byHotel.set(hotelId, group);
  }
  return byHotel;
}

/** Find the min start and max end datetimes for a group of room items. */
function getHotelGroupDateRange(group: ExpandableItem[]): { minStart: string | undefined; maxEnd: string | undefined } {
  const first = group[0]!;
  const startDateTimes = group.map((i) => i.startDateTime).filter(Boolean);
  const endDateTimes = group.map((i) => i.endDateTime).filter(Boolean) as string[];
  const minStart = startDateTimes.length > 0 ? startDateTimes.toSorted((a, b) => compareDateTimes(a, b))[0]! : first.startDateTime;
  const maxEnd = endDateTimes.length > 0 ? endDateTimes.toSorted((a, b) => compareDateTimes(b, a))[0]! : first.endDateTime;
  return { minStart: minStart || undefined, maxEnd: maxEnd || undefined };
}

/**
 * Sums the cost of every room in a hotel group into a single per-stay total. Multiple rooms at the same
 * hotel are separate items, so the grouped check-in event shows their combined cost. Assumes one currency
 * per hotel (the realistic case); any room priced in a different currency is left out of the total.
 */
function sumHotelGroupCost(group: ExpandableItem[]): Cost | undefined {
  const costs = group.map((i) => i.cost).filter((c): c is Cost => !!c);
  if (costs.length === 0) return undefined;
  const currency = costs[0]!.currency;
  const amount = costs.reduce((total, c) => (c.currency === currency ? total + c.amount : total), 0);
  return { amount, currency };
}

/**
 * Groups room itinerary items by hotel and creates one hotel_checkin and one
 * hotel_checkout event per hotel. When flightEvents are provided, check-in
 * times are aligned with flight arrivals on the same day.
 */
export function createGroupedHotelEvents(
  roomItems: ExpandableItem[],
  flightEvents?: ItineraryItemEvent[],
): ItineraryItemEvent[] {
  const events: ItineraryItemEvent[] = [];
  if (roomItems.length === 0) return events;

  // Index flight events by date for O(1) lookup
  const arrivalsByDate = new Map<string, ItineraryItemEvent[]>();
  const departuresByDate = new Map<string, ItineraryItemEvent[]>();
  if (flightEvents) {
    for (const fe of flightEvents) {
      const dateKey = extractEventDateKey(fe.dateTime);
      if (!dateKey) continue;
      if (fe.type === 'flight_arrival') {
        const arr = arrivalsByDate.get(dateKey) ?? [];
        arr.push(fe);
        arrivalsByDate.set(dateKey, arr);
      } else if (fe.type === 'flight_departure') {
        const dep = departuresByDate.get(dateKey) ?? [];
        dep.push(fe);
        departuresByDate.set(dateKey, dep);
      }
    }
  }

  const byHotel = groupRoomItemsByHotel(roomItems);

  for (const [, group] of byHotel) {
    if (group.length === 0) continue;
    const first = group[0]!;
    const hotelName = hotelNameFor(first);
    const itemIds = group.map((i) => i.id).filter(Boolean);
    const stayCost = sumHotelGroupCost(group);
    // Stay status = least-progressed across rooms (any planned → planned, even if others are booked).
    const stayBookingStatus = aggregateBookingStatus(group.map((i) => i.bookingStatus));
    const { minStart, maxEnd } = getHotelGroupDateRange(group);

    if (minStart) {
      // Adjust check-in to align with the latest flight activity on the same day.
      let checkinDateTime = minStart;
      const checkinDate = extractEventDateKey(minStart);
      if (checkinDate) {
        const candidateTimes: string[] = [];

        // Explicit arrival events (overnight flights)
        const arrivals = arrivalsByDate.get(checkinDate);
        if (arrivals) {
          for (const a of arrivals) candidateTimes.push(a.dateTime);
        }

        // Segment arrival times from departure events (same-day flights)
        const departures = departuresByDate.get(checkinDate);
        if (departures) {
          for (const d of departures) {
            const segment = (d.metadata as { segment?: { arrival?: string } })?.segment;
            const segArrival = segment?.arrival;
            if (segArrival && extractEventDateKey(segArrival) === checkinDate) {
              candidateTimes.push(segArrival);
            }
          }
        }

        if (candidateTimes.length > 0) {
          // Use the latest arrival/landing time on the check-in day
          checkinDateTime = candidateTimes.toSorted((a, b) => compareDateTimes(b, a))[0]!;
        }
      }

      events.push({
        id: `hotel_${hotelIdFor(first)}_checkin`,
        title: `Check in at ${hotelName}`,
        dateTime: checkinDateTime,
        type: 'hotel_checkin',
        itemId: first.id,
        sortPriority: 10,
        metadata: { itemIds, originalDateTime: minStart },
        // The stay's total cost (all rooms at this hotel) is shown on the check-in event.
        ...(stayCost ? { cost: stayCost } : {}),
        ...(stayBookingStatus ? { bookingStatus: stayBookingStatus } : {}),
      });
    }
    if (maxEnd) {
      // Place checkout at the start of the day so it always sorts before activities.
      const checkoutDate = extractEventDateKey(maxEnd);
      const checkoutDateTime = checkoutDate ? `${checkoutDate}T00:00:00` : maxEnd;

      events.push({
        id: `hotel_${hotelIdFor(first)}_checkout`,
        title: `Check out from ${hotelName}`,
        dateTime: checkoutDateTime,
        type: 'hotel_checkout',
        itemId: first.id,
        sortPriority: -10,
        metadata: { itemIds, originalDateTime: maxEnd },
      });
    }
  }

  return events.toSorted((a, b) => compareEvents(a, b));
}

/**
 * Computes which hotel the user is staying at for each calendar day.
 * Returns a record keyed by date (YYYY-MM-DD) with an array of HotelStayInfo per day.
 */
export function computeHotelStaysByDay(roomItems: ExpandableItem[]): Record<string, HotelStayInfo[]> {
  const result: Record<string, HotelStayInfo[]> = {};
  if (roomItems.length === 0) return result;

  const byHotel = groupRoomItemsByHotel(roomItems);

  for (const [, group] of byHotel) {
    if (group.length === 0) continue;
    const first = group[0]!;
    const hotelId = hotelIdFor(first);
    const hotelName = hotelNameFor(first);
    const itemIds = group.map((i) => i.id).filter(Boolean);
    const destination = hotelDestinationFor(first);
    const { minStart, maxEnd } = getHotelGroupDateRange(group);

    if (!minStart) continue;

    const startDateStr = extractEventDateKey(minStart);
    if (!startDateStr) continue;
    const startDate = parseISO(startDateStr);
    if (!isValid(startDate)) continue;

    // If no end date, treat as single-night stay (check-in and check-out on next day)
    const endDateStr = maxEnd ? extractEventDateKey(maxEnd) : undefined;
    const endDate = endDateStr ? parseISO(endDateStr) : addDays(startDate, 1);
    if (!isValid(endDate)) continue;

    const totalDays = differenceInDays(endDate, startDate) + 1; // inclusive of both start and end

    for (let i = 0; i < totalDays; i++) {
      const currentDate = addDays(startDate, i);
      const dateKey = format(currentDate, 'yyyy-MM-dd');
      const isCheckinDay = i === 0;
      const isCheckoutDay = i === totalDays - 1;

      (result[dateKey] ??= []).push({
        hotelId,
        hotelName,
        itemId: first.id,
        itemIds,
        isCheckinDay,
        isCheckoutDay,
        ...(destination ? { destination } : {}),
      });
    }
  }

  return result;
}

/**
 * Expands and merges all itinerary items into a single, sorted event list.
 * Non-room items expand individually; room items collapse to grouped hotel
 * check-in/out events positioned relative to flights.
 */
export function expandAllEvents(items: ExpandableItem[]): ItineraryItemEvent[] {
  const roomItems = items.filter((i) => i.type === 'room');
  const nonRoomItems = items.filter((i) => i.type !== 'room');

  const nonRoomEvents = nonRoomItems.flatMap((item) => expandItineraryItem(item));
  const flightEvents = nonRoomEvents.filter(
    (e) => e.type === 'flight_departure' || e.type === 'flight_arrival',
  );
  const roomEvents = createGroupedHotelEvents(roomItems, flightEvents);

  return [...nonRoomEvents, ...roomEvents].toSorted((a, b) => compareEvents(a, b));
}

/**
 * Groups events by their local calendar-date key. Days listed in `dateKeys`
 * are seeded with empty arrays (so empty days still render); any event whose
 * day falls outside that list is added under its own key. Each day's events
 * are sorted by {@link compareEvents}.
 */
export function groupEventsByDay(
  events: ItineraryItemEvent[],
  dateKeys: string[] = [],
): Record<string, ItineraryItemEvent[]> {
  const grouped: Record<string, ItineraryItemEvent[]> = {};
  for (const dateKey of dateKeys) grouped[dateKey] = [];

  for (const event of events) {
    const dateKey = extractEventDateKey(event.dateTime);
    if (!dateKey) continue;
    (grouped[dateKey] ??= []).push(event);
  }

  for (const dateKey in grouped) {
    grouped[dateKey] = grouped[dateKey]!.toSorted((a, b) => compareEvents(a, b));
  }
  return grouped;
}

/**
 * Determines whether a day is a travel day: it has no events of its own and the
 * previous day ends on a flight departure (the traveler is mid-air / skipped a
 * date across time zones). Assumes each day's events are pre-sorted.
 */
export function isTravelDay(
  eventsByDay: Record<string, ItineraryItemEvent[]>,
  currentDateKey: string,
  previousDateKey: string,
): boolean {
  const events = eventsByDay[currentDateKey];
  if (events?.length === 0) {
    const previousDayEvents = eventsByDay[previousDateKey];
    if (previousDayEvents && previousDayEvents.length > 0) {
      return previousDayEvents.at(-1)!.type === 'flight_departure';
    }
  }
  return false;
}

/**
 * Computes the ordered, contiguous list of calendar dates the itinerary spans.
 * The range starts/ends at the trip's official dates, extended outward by any
 * event that falls before/after them. Returns the date keys in order.
 */
export function computeDateKeyRange(
  events: ItineraryItemEvent[],
  tripStartDate?: string,
  tripEndDate?: string,
): string[] {
  let startKey = tripStartDate ? extractEventDateKey(tripStartDate) ?? tripStartDate : undefined;
  let endKey = tripEndDate ? extractEventDateKey(tripEndDate) ?? tripEndDate : undefined;

  for (const event of events) {
    const key = extractEventDateKey(event.dateTime);
    if (!key) continue;
    if (!startKey || key < startKey) startKey = key;
    if (!endKey || key > endKey) endKey = key;
  }

  if (!startKey || !endKey) return [];

  const start = parseISO(startKey);
  const end = parseISO(endKey);
  if (!isValid(start) || !isValid(end)) return [];

  const totalDays = differenceInDays(end, start) + 1;
  if (totalDays <= 0) return [];

  const dateKeys: string[] = [];
  for (let i = 0; i < totalDays; i++) {
    dateKeys.push(format(addDays(start, i), 'yyyy-MM-dd'));
  }
  return dateKeys;
}

/** A single destination arrival derived from a flight's final outbound/return segment. */
interface FlightArrivalSignal {
  dateKey: string;
  arrivalDateTime: string;
  descriptor: DestinationDescriptor;
  countryCode?: string;
}

/** Builds a destination descriptor from a flight segment's destination airport. */
function descriptorFromSegmentDestination(dest: FlightSegment['destination']): DestinationDescriptor {
  const cityName = dest.city_name ?? undefined;
  const countryName = dest.country_name ?? undefined;
  const name = cityName ?? countryName;
  return {
    ...(cityName ? { cityName } : {}),
    ...(dest.country_code ? { countryCode: dest.country_code } : {}),
    ...(countryName ? { countryName } : {}),
    ...(name ? { name } : {}),
  };
}

/**
 * Derives one destination arrival signal per flight leg from the flight option's
 * FINAL outbound segment (and, for round trips, the final return segment). Reading
 * the segments directly — not the expanded events — sidesteps the overnight-only
 * arrival-event gap and never treats a layover as a destination.
 */
function flightArrivalSignals(flightOption: FlightOption): FlightArrivalSignal[] {
  const legs: FlightSegment[][] = [];
  const outbound =
    flightOption.outboundSegments && flightOption.outboundSegments.length > 0
      ? flightOption.outboundSegments
      : flightOption.segments ?? [];
  if (outbound.length > 0) legs.push(outbound);
  if (flightOption.returnSegments && flightOption.returnSegments.length > 0) legs.push(flightOption.returnSegments);

  const signals: FlightArrivalSignal[] = [];
  for (const leg of legs) {
    const last = leg.at(-1);
    if (!last?.destination || !last.arrival) continue;
    const dateKey = extractEventDateKey(last.arrival);
    if (!dateKey) continue;
    signals.push({
      dateKey,
      arrivalDateTime: last.arrival,
      descriptor: descriptorFromSegmentDestination(last.destination),
      ...(last.destination.country_code ? { countryCode: last.destination.country_code } : {}),
    });
  }
  return signals;
}

/** Country code the trip departs from (first outbound segment of the earliest flight). */
function computeOriginCountryCode(flightItems: ExpandableItem[]): string | undefined {
  const sorted = flightItems
    .filter((i) => i.flightOption)
    .toSorted((a, b) => compareDateTimes(a.startDateTime, b.startDateTime));
  for (const item of sorted) {
    const fo = item.flightOption!;
    const outbound = fo.outboundSegments && fo.outboundSegments.length > 0 ? fo.outboundSegments : fo.segments ?? [];
    const origin = outbound[0]?.origin?.country_code;
    if (origin) return origin;
  }
  return undefined;
}

/** Stable coalescing key for a destination descriptor. */
function destinationKey(descriptor: DestinationDescriptor | undefined): string {
  if (!descriptor) return 'origin';
  if (descriptor.destinationId) return `dest:${descriptor.destinationId}`;
  const key = `${descriptor.countryCode ?? ''}:${descriptor.cityName ?? descriptor.name ?? ''}`;
  return key === ':' ? 'origin' : key;
}

/** Most-recent flight arrival descriptor at or before the given date key (signals pre-sorted by arrival). */
function mostRecentFlightDescriptor(
  signals: FlightArrivalSignal[],
  dateKey: string,
): DestinationDescriptor | undefined {
  let result: DestinationDescriptor | undefined;
  for (const signal of signals) {
    if (signal.dateKey <= dateKey) result = signal.descriptor;
    else break;
  }
  return result;
}

/**
 * Partitions a trip's ordered calendar days into contiguous destination groups,
 * each with a date window and per-currency cost subtotals.
 *
 * Signal precedence per day: (1) a hotel night's pre-resolved `destination`
 * (check-in stay wins on a back-to-back transition day); (2) the most-recent
 * flight arrival, forward-filled — return-to-origin legs are dropped so no "Home"
 * group appears; (3) a dated `plan` entry (working-memory-derived) whose window
 * contains the day, so a not-yet-booked multi-destination trip still date-splits.
 * Leading undetermined days back-fill into the first determined destination;
 * trailing/gap days forward-fill from the previous one. A trip with no signal at
 * all collapses to a single descriptor-less group.
 *
 * Costs: each item is assigned to the group owning its START-date day and summed
 * per currency (one {@link Cost} per currency — no FX conversion). A multi-night
 * hotel counts once in its check-in group; a flight counts in its departure-day
 * group; items with no `cost` and cancelled bookings are skipped (a cancelled booking is no longer part
 * of what the trip costs, ELARA-972).
 */
export function groupDaysByDestination(
  orderedDateKeys: string[],
  items: ExpandableItem[],
  plan: PlannedDestination[] = [],
): DestinationGroup[] {
  if (orderedDateKeys.length === 0) return [];

  const roomItems = items.filter((i) => i.type === 'room');
  const flightItems = items.filter((i) => i.type === 'flight' && i.flightOption);

  const hotelStays = computeHotelStaysByDay(roomItems);
  const originCountryCode = computeOriginCountryCode(flightItems);
  const flightSignals = flightItems
    .flatMap((i) => (i.flightOption ? flightArrivalSignals(i.flightOption) : []))
    // Drop the return-home leg so arriving back in the origin country isn't a destination.
    .filter((signal) => !(originCountryCode && signal.countryCode && signal.countryCode === originCountryCode))
    .toSorted((a, b) => compareDateTimes(a.arrivalDateTime, b.arrivalDateTime));

  // Resolve each day's destination by precedence: hotel → flight → plan.
  const perDay = orderedDateKeys.map<{ descriptor?: DestinationDescriptor; source?: DestinationGroup['source'] }>(
    (dateKey) => {
      const stays = hotelStays[dateKey];
      const hotelDest =
        stays?.find((s) => s.isCheckinDay && s.destination)?.destination ??
        stays?.find((s) => s.destination)?.destination;
      if (hotelDest) return { descriptor: hotelDest, source: 'hotel' };

      const flightDest = mostRecentFlightDescriptor(flightSignals, dateKey);
      if (flightDest) return { descriptor: flightDest, source: 'flight' };

      const planned = plan.find((p) => p.startDateKey <= dateKey && dateKey <= p.endDateKey);
      if (planned) return { descriptor: planned.descriptor, source: 'plan' };

      return {};
    },
  );

  // Back-fill leading undetermined days with the first determined destination.
  const firstDetermined = perDay.find((d) => d.descriptor);
  if (firstDetermined) {
    for (const day of perDay) {
      if (day.descriptor) break;
      day.descriptor = firstDetermined.descriptor;
      day.source = firstDetermined.source;
    }
  }
  // Forward-fill gap/trailing undetermined days from the previous determined day.
  for (let i = 1; i < perDay.length; i++) {
    if (!perDay[i]!.descriptor && perDay[i - 1]!.descriptor) {
      perDay[i]!.descriptor = perDay[i - 1]!.descriptor;
      perDay[i]!.source = perDay[i - 1]!.source;
    }
  }

  // Coalesce consecutive days sharing a destination key.
  const groups: DestinationGroup[] = [];
  for (let i = 0; i < orderedDateKeys.length; i++) {
    const dateKey = orderedDateKeys[i]!;
    const { descriptor, source } = perDay[i]!;
    const key = destinationKey(descriptor);
    const last = groups.at(-1);
    if (last && last.key === key) {
      last.dateKeys.push(dateKey);
      last.endDateKey = dateKey;
    } else {
      groups.push({
        key,
        ...(descriptor ? { descriptor } : {}),
        dateKeys: [dateKey],
        startDateKey: dateKey,
        endDateKey: dateKey,
        ...(source ? { source } : {}),
        costs: [],
      });
    }
  }

  // Sum item costs per currency into the group owning each item's start day.
  const groupByDateKey = new Map<string, DestinationGroup>();
  for (const group of groups) for (const dateKey of group.dateKeys) groupByDateKey.set(dateKey, group);

  const costAcc = new Map<DestinationGroup, Map<string, number>>();
  for (const item of items) {
    if (!item.cost || isCancelledBooking(item)) continue;
    const dateKey = extractEventDateKey(item.startDateTime);
    if (!dateKey) continue;
    const group = groupByDateKey.get(dateKey);
    if (!group) continue;
    const byCurrency = costAcc.get(group) ?? new Map<string, number>();
    byCurrency.set(item.cost.currency, (byCurrency.get(item.cost.currency) ?? 0) + item.cost.amount);
    costAcc.set(group, byCurrency);
  }
  for (const [group, byCurrency] of costAcc) {
    group.costs = [...byCurrency.entries()]
      .map(([currency, amount]) => ({ amount, currency }))
      .toSorted((a, b) => a.currency.localeCompare(b.currency));
  }

  return groups;
}

/** Output of {@link expandItinerary}: the merged events plus the canonical day list. */
export interface ExpandedItinerary {
  /** All display events, expanded and sorted. */
  events: ItineraryItemEvent[];
  /** Events grouped by calendar-date key (every day in `days` is present). */
  eventsByDay: Record<string, ItineraryItemEvent[]>;
  /** Canonical ordered day list with day numbers and travel-day flags. */
  days: ItineraryDay[];
  /** Contiguous destination groups (hotel/flight/plan-anchored) with date windows + costs. */
  destinationGroups: DestinationGroup[];
}

/**
 * Top-level entry point: expands every item, groups by day across the trip's
 * date range, and produces the canonical day list (date key + day number +
 * travel-day flag). All consumers — web render, the wire flow, and the
 * day-summaries prompt — go through here so the day structure is identical.
 */
export function expandItinerary(
  items: ExpandableItem[],
  options: { tripStartDate?: string; tripEndDate?: string } = {},
): ExpandedItinerary {
  const events = expandAllEvents(items);
  const dateKeys = computeDateKeyRange(events, options.tripStartDate, options.tripEndDate);
  const eventsByDay = groupEventsByDay(events, dateKeys);

  const days: ItineraryDay[] = dateKeys.map((dateKey, index) => ({
    dateKey,
    dayNumber: index + 1,
    isTravelDay: isTravelDay(eventsByDay, dateKey, index > 0 ? dateKeys[index - 1]! : ''),
  }));

  const destinationGroups = groupDaysByDestination(dateKeys, items);

  return { events, eventsByDay, days, destinationGroups };
}
